import Link from '@carvana/ds-link';
import breakpoints from '@carvana/merch-tailwind/breakpoints';
import cx from 'classnames';
import { appAssetsCdnUrl } from '~/constants/environment';
import { type NewCarVariant } from '~/utilities/srp/new-car/getNewCarVariant';
import toLowerKebabCase from '~/utilities/toLowerKebabCase';
import { Wrapper } from '../PromoBanner.styles';

type OwnProps = {
  bannerName: string;
  href: string;
  make: string;
  variant: NewCarVariant;
};

const makeLogo: Record<string, string> = {
  chrysler: `${appAssetsCdnUrl}/images/new-car/chrysler/logo.svg`,
  dodge: `${appAssetsCdnUrl}/images/new-car/dodge/logo.svg`,
  jeep: `${appAssetsCdnUrl}/images/new-car/jeep/logo.svg`,
  ram: `${appAssetsCdnUrl}/images/new-car/ram/hero-icon.svg`
};

const makeLogoStyles: Record<string, string> = {
  chrysler: 'w-[65px] sm:w-[150px] mr-xl sm:mr-0',
  dodge: 'w-[65px] sm:w-[150px] lg:w-[190px] mr-xl sm:mr-0',
  jeep: 'w-[70px] sm:w-[90px] lg:w-[115px] mr-xl sm:mr-0',
  ram: 'w-[40px] sm:w-[75px] lg:w-[88px] mr-xl sm:mr-0'
};

const makeContainerStyles: Record<string, string> = {
  chrysler: 'pr-0',
  dodge: 'pr-0'
};

const defaultLogoStyle = 'w-[56px] sm:w-[100px] lg:w-[150px]';

const backgroundDesktopSrc = `${appAssetsCdnUrl}/images/new-car/hero-make-background-large.svg`;
const backgroundDesktopXlSrc = `${appAssetsCdnUrl}/images/helios/promoBanners/ncc/make/background-desktop.svg`;
const backgroundMobileSrc = `${appAssetsCdnUrl}/images/helios/promoBanners/ncc/make/background-mobile.svg`;

export default function MakeBanner({
  bannerName,
  href,
  make,
  variant
}: OwnProps) {
  const normalizedMake = toLowerKebabCase(make);
  const shopAnalyticsAttributes = JSON.stringify({
    category: bannerName,
    label: 'Select Color, Options & More',
    variant
  });

  return (
    <Wrapper className="bg-background-strong">
      <picture aria-hidden="true">
        <source
          media={`(min-width: ${breakpoints.xl}px)`}
          srcSet={backgroundDesktopXlSrc}
        />
        <source
          media={`(min-width: ${breakpoints.sm}px)`}
          srcSet={backgroundDesktopSrc}
        />
        <img
          alt=""
          className="absolute inset-0 size-full object-cover"
          src={backgroundMobileSrc}
        />
      </picture>

      <div className="relative z-1 flex flex-1 flex-col justify-center gap-lg py-2xl pl-2xl sm:pr-0 lg:py-4xl lg:pl-4xl">
        <h4 className="t-heading-sm max-w-[185px] text-text-inverse sm:t-heading-xl sm:max-w-[350px] md:max-w-[290px] lg:t-heading-2xl lg:max-w-full">
          Build your perfect new{' '}
          <span className="capitalize text-yellow-400">{make}</span>
        </h4>
        <p className="hidden max-w-[348px] t-body-sm text-text-inverse sm:block lg:max-w-[450px] xl:max-w-full">
          All brand new models — pickup or delivery as soon as today!
        </p>
        <div>
          <Link
            className="text-text-inverse"
            data-adobe-id="select-color-options-more"
            data-analytics-attributes={shopAnalyticsAttributes}
            data-analytics-id="Banner"
            data-testid="promo-banner-select-color-options-more"
            href={href}
            size="sm"
          >
            Select Color, Options & More
          </Link>
        </div>
      </div>

      <div
        className={cx(
          'relative z-1 flex sm:w-2/5 items-center justify-center',
          makeContainerStyles[normalizedMake]
        )}
      >
        <img
          alt={`${make} logo`}
          className={cx(
            'block object-contain',
            makeLogoStyles[normalizedMake] ?? defaultLogoStyle
          )}
          data-testid="make-logo"
          src={makeLogo[normalizedMake]}
        />
      </div>
    </Wrapper>
  );
}
